import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

/** Convert a base64url string (VAPID public key) to a Uint8Array. */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function isIosSafari() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

async function getRegistration() {
  let reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) {
    reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  }
  await navigator.serviceWorker.ready;
  return reg;
}

export async function getCurrentSubscription() {
  if (!pushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration("/");
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

export async function subscribePush() {
  if (!pushSupported()) throw new Error("Push notifications aren't supported on this browser.");
  if (isIosSafari() && !isStandalone()) {
    throw new Error(
      "On iPhone/iPad, please tap Share → Add to Home Screen and open Tides Hub from the home-screen icon to enable notifications."
    );
  }

  // 1. Request browser permission
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notification permission was denied.");

  // 2. Register the service worker
  const reg = await getRegistration();

  // 3. Fetch VAPID public key from backend
  const { data } = await axios.get(`${API_URL}/push/public-key`);
  const publicKey = data?.public_key;
  if (!publicKey) throw new Error("Push isn't configured on the server yet.");

  // 4. Subscribe
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    // If the existing sub is on a different VAPID key, unsubscribe + re-create
    const oldKey = sub.options?.applicationServerKey;
    if (oldKey) {
      try { await sub.unsubscribe(); } catch {}
      sub = null;
    }
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  // 5. Register on backend
  await axios.post(`${API_URL}/push/subscribe`, {
    ...sub.toJSON(),
    userAgent: navigator.userAgent,
  });

  return sub;
}

export async function unsubscribePush() {
  const sub = await getCurrentSubscription();
  if (!sub) return;
  try {
    await axios.post(`${API_URL}/push/unsubscribe`, { endpoint: sub.endpoint });
  } catch {}
  try { await sub.unsubscribe(); } catch {}
}

export async function sendTestPush() {
  const { data } = await axios.post(`${API_URL}/push/test`, {
    title: "Tides Hub",
    body: "Notifications are working ✓",
  });
  return data;
}
